/**
 * Badge component with Dashboard3 styling and tone variants
 *
 * Note: Must be "use client" because it uses useThemeTokens and useInteractiveTokens hooks
 *
 * @example
 * ```tsx
 * <Badge tone="success">Active</Badge>
 * <Badge tone="danger">Inactive</Badge>
 * ```
 */

"use client";

import {
  Badge as ChakraBadge,
  type BadgeProps as ChakraBadgeProps,
} from "@chakra-ui/react";
import { useThemeTokens } from "@/lib/theme/tokens";
import { useInteractiveTokens } from "@/lib/theme/tokens/interactive";

export interface BadgeProps extends ChakraBadgeProps {
  /**
   * Badge tone variant
   * - neutral: Default neutral badge
   * - success: Success state (uses accent muted)
   * - premium: Premium/highlight state
   * - danger: Danger/warning state
   * - info: Informational state
   */
  tone?: "neutral" | "success" | "premium" | "danger" | "info";
}

export const Badge = ({ tone = "neutral", ...props }: BadgeProps) => {
  const theme = useThemeTokens();
  const interactive = useInteractiveTokens();

  // Use Accent Muted for badges (secondary highlights)
  const bgMap = {
    neutral: interactive.neutral.bgStrong,
    success: theme.accent.mutedBg,
    premium: theme.accent.mutedBg,
    danger: theme.accent.mutedBg,
    info: interactive.neutral.bgStrong,
  } as const;

  const colorMap = {
    neutral: theme.text.secondary,
    success: theme.accent.muted,
    premium: theme.accent.muted,
    danger: theme.accent.muted,
    info: theme.accent.muted,
  } as const;

  return (
    <ChakraBadge
      px={3}
      py={1}
      borderRadius="full"
      fontSize="xs"
      fontWeight="normal"
      textTransform="none"
      bg={bgMap[tone]}
      color={colorMap[tone]}
      {...props}
    />
  );
};
